import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createSelector } from '@reduxjs/toolkit';
import type { RootState } from './index';
import { clearFunnelMap } from './united-canvas/unitedCanvasReducer';
import type { AiChatMessage } from '../components/common/ai-chat/aiChatTypes';
import { ChatCompletionRequestMessageRole } from '@server/ai';

export type MetadataMappingStatus = 'idle' | 'loading' | 'completedFetchingData' | 'error';

// Extended message type for metadata mapping with undo support
export interface MetadataMappingMessage extends AiChatMessage {
  historyIndex?: number; // redux-undo past.length when this user message was sent
}

export interface MetadataMappingFunnelState {
  status: MetadataMappingStatus;
  error?: string;
  startedAt?: number;
  messages: MetadataMappingMessage[];
}

export interface MetadataMappingState {
  funnelMaps: {
    [funnelMapId: string]: MetadataMappingFunnelState;
  };
}

const getDefaultMetadataMappingState = (): MetadataMappingFunnelState => ({
  status: 'idle',
  error: undefined,
  startedAt: undefined,
  messages: [],
});

const initialState: MetadataMappingState = {
  funnelMaps: {},
};

export const metadataMappingSlice = createSlice({
  name: 'metadataMapping',
  initialState,
  reducers: {
    setMetadataMappingLoading: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
        isLoading: boolean;
      }>,
    ) => {
      const { funnelMapId, isLoading } = action.payload;
      if (!state.funnelMaps[funnelMapId]) {
        state.funnelMaps[funnelMapId] = getDefaultMetadataMappingState();
      }
      state.funnelMaps[funnelMapId].status = isLoading ? 'loading' : 'idle';
      state.funnelMaps[funnelMapId].error = undefined;
      state.funnelMaps[funnelMapId].startedAt = isLoading ? Date.now() : undefined;
    },
    setMetadataMappingCompleted: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
      }>,
    ) => {
      const { funnelMapId } = action.payload;
      if (!state.funnelMaps[funnelMapId]) {
        state.funnelMaps[funnelMapId] = getDefaultMetadataMappingState();
      }
      state.funnelMaps[funnelMapId].status = 'completedFetchingData';
      state.funnelMaps[funnelMapId].error = undefined;
      state.funnelMaps[funnelMapId].startedAt = undefined;
    },
    setMetadataMappingError: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
        error: string;
      }>,
    ) => {
      const { funnelMapId, error } = action.payload;
      if (!state.funnelMaps[funnelMapId]) {
        state.funnelMaps[funnelMapId] = getDefaultMetadataMappingState();
      }
      state.funnelMaps[funnelMapId].status = 'error';
      state.funnelMaps[funnelMapId].error = error;
      state.funnelMaps[funnelMapId].startedAt = undefined;
    },
    clearMetadataMappingState: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
      }>,
    ) => {
      const { funnelMapId } = action.payload;
      delete state.funnelMaps[funnelMapId];
    },
    cleanupExpiredMetadataMappingStates: (state) => {
      const now = Date.now();
      const TIMEOUT_DURATION = 10 * 60 * 1000;

      Object.keys(state.funnelMaps).forEach((funnelMapId) => {
        const funnelState = state.funnelMaps[funnelMapId];
        if (
          funnelState.status === 'loading' &&
          funnelState.startedAt &&
          now - funnelState.startedAt > TIMEOUT_DURATION
        ) {
          state.funnelMaps[funnelMapId] = {
            status: 'error',
            error: 'Metadata mapping request timed out. Please try again.',
            startedAt: undefined,
            messages: state.funnelMaps[funnelMapId].messages ?? [],
          };
        }
      });
    },
    addChatMessage: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
        message: MetadataMappingMessage;
      }>,
    ) => {
      const { funnelMapId, message } = action.payload;
      if (!state.funnelMaps[funnelMapId]) {
        state.funnelMaps[funnelMapId] = getDefaultMetadataMappingState();
      }
      if (!state.funnelMaps[funnelMapId].messages) {
        state.funnelMaps[funnelMapId].messages = [];
      }
      state.funnelMaps[funnelMapId].messages.push(message);
    },
    removeMessagesFromIndex: (
      state,
      action: PayloadAction<{
        funnelMapId: string;
        fromIndex: number;
      }>,
    ) => {
      const { funnelMapId, fromIndex } = action.payload;
      const funnelState = state.funnelMaps[funnelMapId];
      if (!funnelState) return;
      funnelState.messages = funnelState.messages.slice(0, fromIndex);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearFunnelMap.type, (state) => {
      state.funnelMaps = {};
    });
  },
});

export const {
  setMetadataMappingLoading,
  setMetadataMappingCompleted,
  setMetadataMappingError,
  clearMetadataMappingState,
  cleanupExpiredMetadataMappingStates,
  addChatMessage,
  removeMessagesFromIndex,
} = metadataMappingSlice.actions;

export const selectMetadataMappingState = (funnelMapId: string) => (state: RootState) =>
  state.metadataMapping.funnelMaps[funnelMapId] || getDefaultMetadataMappingState();

export const selectMetadataMappingStatus = (funnelMapId: string) => (state: RootState) =>
  state.metadataMapping.funnelMaps[funnelMapId]?.status || 'idle';

export const selectMetadataMappingLoading = (funnelMapId: string) => (state: RootState) =>
  state.metadataMapping.funnelMaps[funnelMapId]?.status === 'loading';

export const selectMetadataMappingCompleted = (funnelMapId: string) => (state: RootState) =>
  state.metadataMapping.funnelMaps[funnelMapId]?.status === 'completedFetchingData';

export const selectMetadataMappingError = (funnelMapId: string) => (state: RootState) =>
  state.metadataMapping.funnelMaps[funnelMapId]?.error;

export const selectHasAnyMetadataMappingInProgress = () => (state: RootState) => {
  return Object.values(state.metadataMapping.funnelMaps).some(
    (funnelMapState) => funnelMapState.status === 'loading',
  );
};

const EMPTY_MESSAGES_ARRAY: MetadataMappingMessage[] = [];

export const selectMetadataMappingMessages = (funnelMapId: string) => (state: RootState) =>
  state.metadataMapping.funnelMaps[funnelMapId]?.messages ?? EMPTY_MESSAGES_ARRAY;

export const selectLastUserMessageHistoryIndex = (funnelMapId: string) =>
  createSelector([selectMetadataMappingMessages(funnelMapId)], (messages) => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === ChatCompletionRequestMessageRole.USER) {
        return messages[i].historyIndex ?? null;
      }
    }
    return null;
  });

export const selectLastUserMessageIndex = (funnelMapId: string) =>
  createSelector([selectMetadataMappingMessages(funnelMapId)], (messages) => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === ChatCompletionRequestMessageRole.USER) {
        return i;
      }
    }
    return null;
  });

export const selectHasUserMessages = (funnelMapId: string) =>
  createSelector([selectMetadataMappingMessages(funnelMapId)], (messages) =>
    messages.some((m) => m.role === ChatCompletionRequestMessageRole.USER),
  );

export default metadataMappingSlice.reducer;
